/**
 * The vocabulary that turns a database change into something a shop owner can read.
 *
 * A merchant should never meet `products.product_variants`, a UUID, or a column
 * name. This maps each audited table to a business entity, and each column to a
 * label and a value formatter.
 *
 * It doubles as the **allowlist**: a table with no entry here is not shown to
 * merchants at all. Store scoping alone is not the boundary — a newly enrolled
 * internal table would otherwise appear in a merchant's history the day it is
 * added, in system vocabulary. Absence means hidden, not "shown raw".
 */

export type MerchantCategory =
  | 'Catalog'
  | 'Pricing'
  | 'Orders'
  | 'Stock'
  | 'Team'
  | 'Settings';

export interface MerchantEntity {
  category: MerchantCategory;
  /** Singular, lower case — used to build "Updated product". */
  entity: string;
  label: string;
  /** Tried in order to name the record; falls back to the id's first segment. */
  nameFields: string[];
  /** Column → human label. Columns absent here still show, prettified. */
  fields?: Record<string, string>;
  /** Columns stored in paise, rendered as rupees. */
  money?: string[];
  /** Columns never shown to a merchant even though the table is visible. */
  hidden?: string[];
}

export const MERCHANT_ENTITIES: Record<string, MerchantEntity> = {
  // ── Catalog ──────────────────────────────────────────────────────────
  'products.products': {
    category: 'Catalog', entity: 'product', label: 'Product',
    nameFields: ['title', 'name', 'slug'],
    fields: {
      title: 'Name', slug: 'URL slug', description: 'Description',
      status: 'Status', active: 'Active', brandId: 'Brand', categoryId: 'Category',
      hsnCode: 'HSN code', taxSlabId: 'Tax slab', unitOfMeasure: 'Unit',
    },
  },
  'products.product_variants': {
    category: 'Catalog', entity: 'variant', label: 'Variant',
    nameFields: ['sku', 'title', 'name'],
    fields: { sku: 'SKU', price: 'Price', mrp: 'MRP', compareAtPrice: 'Compare-at price', barcode: 'Barcode', weight: 'Weight' },
    money: ['price', 'mrp', 'compareAtPrice'],
  },
  'products.product_images': {
    category: 'Catalog', entity: 'image', label: 'Product image',
    nameFields: ['altText', 'url'],
    fields: { url: 'Image', altText: 'Alt text', position: 'Position' },
  },
  'master.categories': {
    category: 'Catalog', entity: 'category', label: 'Category',
    nameFields: ['name', 'title', 'slug'],
    fields: { name: 'Name', slug: 'URL slug', parentId: 'Parent category' },
  },
  'products.brands': {
    category: 'Catalog', entity: 'brand', label: 'Brand',
    nameFields: ['name', 'slug'],
  },
  'products.collections': {
    category: 'Catalog', entity: 'collection', label: 'Collection',
    nameFields: ['title', 'name', 'slug'],
  },

  // ── Pricing ──────────────────────────────────────────────────────────
  'marketing.discount_codes': {
    category: 'Pricing', entity: 'discount code', label: 'Discount code',
    nameFields: ['code'],
    fields: { code: 'Code', value: 'Value', type: 'Type', minOrderValue: 'Minimum order', usageLimit: 'Usage limit', expiresAt: 'Expires' },
    money: ['minOrderValue'],
  },
  'marketing.promotions': {
    category: 'Pricing', entity: 'promotion', label: 'Promotion',
    nameFields: ['name', 'title'],
  },
  'marketing.promotion_rules': {
    category: 'Pricing', entity: 'promotion rule', label: 'Promotion rule',
    nameFields: ['name'],
  },
  'master.tax_slab_profiles': {
    category: 'Pricing', entity: 'tax slab', label: 'Tax slab',
    nameFields: ['name', 'code'],
    fields: { rate: 'Rate', name: 'Name' },
  },

  // ── Orders ───────────────────────────────────────────────────────────
  'sales.orders': {
    category: 'Orders', entity: 'order', label: 'Order',
    nameFields: ['orderNumber', 'number', 'reference'],
    fields: {
      status: 'Status', paymentStatus: 'Payment status', fulfillmentStatus: 'Fulfilment status',
      total: 'Total', subtotal: 'Subtotal', shippingTotal: 'Shipping', taxTotal: 'Tax', discountTotal: 'Discount',
    },
    money: ['total', 'subtotal', 'shippingTotal', 'taxTotal', 'discountTotal'],
  },
  'sales.order_items': {
    category: 'Orders', entity: 'order item', label: 'Order item',
    nameFields: ['title', 'name', 'sku'],
    fields: { quantity: 'Quantity', unitPrice: 'Unit price', total: 'Line total' },
    money: ['unitPrice', 'total'],
  },
  'sales.order_status_history': {
    category: 'Orders', entity: 'order status', label: 'Order status',
    nameFields: ['status'],
  },
  'sales.payments': {
    category: 'Orders', entity: 'payment', label: 'Payment',
    nameFields: ['reference', 'providerReference'],
    fields: { amount: 'Amount', status: 'Status', provider: 'Provider' },
    money: ['amount'],
  },
  'sales.payment_transactions': {
    category: 'Orders', entity: 'payment transaction', label: 'Payment transaction',
    nameFields: ['reference'],
    money: ['amount'],
  },
  'sales.refunds': {
    category: 'Orders', entity: 'refund', label: 'Refund',
    nameFields: ['reference'],
    fields: { amount: 'Amount', reason: 'Reason', status: 'Status' },
    money: ['amount'],
  },
  'sales.refund_items': {
    category: 'Orders', entity: 'refund item', label: 'Refund item',
    nameFields: ['title'], money: ['amount'],
  },
  'sales.shipments': {
    category: 'Orders', entity: 'shipment', label: 'Shipment',
    nameFields: ['trackingNumber', 'reference'],
    fields: { trackingNumber: 'Tracking number', carrier: 'Carrier', status: 'Status' },
  },

  // ── Stock ────────────────────────────────────────────────────────────
  'inventory.inventory_items': {
    category: 'Stock', entity: 'stock item', label: 'Stock item',
    nameFields: ['sku', 'name'],
  },
  'inventory.inventory_levels': {
    category: 'Stock', entity: 'stock level', label: 'Stock level',
    nameFields: ['sku'],
    fields: { available: 'Available', committed: 'Committed', onHand: 'On hand' },
  },
  'inventory.inventory_movements': {
    category: 'Stock', entity: 'stock movement', label: 'Stock movement',
    nameFields: ['reason', 'changeType'],
    fields: { quantityDelta: 'Change', reason: 'Reason', changeType: 'Type' },
  },
  'products.inventory': {
    category: 'Stock', entity: 'stock', label: 'Stock',
    nameFields: ['sku'],
    fields: { quantity: 'Quantity' },
  },

  // ── Team ─────────────────────────────────────────────────────────────
  'store.store_staff': {
    category: 'Team', entity: 'team member', label: 'Team member',
    nameFields: ['email', 'name'],
    fields: { role: 'Role', isActive: 'Active', email: 'Email' },
    // The invite token is already redacted at capture; keep it out of sight too.
    hidden: ['inviteToken', 'inviteTokenExpiresAt'],
  },
  'store.store_staff_permissions': {
    category: 'Team', entity: 'permission', label: 'Permission',
    nameFields: ['permission'],
  },

  // ── Settings ─────────────────────────────────────────────────────────
  'store.store_settings': {
    category: 'Settings', entity: 'store setting', label: 'Store setting',
    nameFields: ['key', 'name'],
  },
  'store.store_branding': {
    category: 'Settings', entity: 'branding', label: 'Branding',
    nameFields: ['name'],
  },
  'store.payment_provider_settings': {
    category: 'Settings', entity: 'payment provider', label: 'Payment provider',
    nameFields: ['provider'],
    fields: { provider: 'Provider', isActive: 'Enabled' },
  },
  'store.shipping_settings': {
    category: 'Settings', entity: 'shipping setting', label: 'Shipping settings',
    nameFields: ['name'],
  },
  'store.shipping_rates': {
    category: 'Settings', entity: 'shipping rate', label: 'Shipping rate',
    nameFields: ['name'], money: ['price', 'rate'],
  },
  'store.shipping_zones': {
    category: 'Settings', entity: 'shipping zone', label: 'Shipping zone',
    nameFields: ['name'],
  },
  'store.shipping_channels': {
    category: 'Settings', entity: 'shipping channel', label: 'Shipping channel',
    nameFields: ['name'],
  },
  'store.stores': {
    category: 'Settings', entity: 'store', label: 'Store',
    nameFields: ['name', 'domain'],
    fields: { name: 'Name', isActive: 'Active', currency: 'Currency' },
  },
};

export const MERCHANT_CATEGORIES: MerchantCategory[] = [
  'Catalog', 'Pricing', 'Orders', 'Stock', 'Team', 'Settings',
];

export function entityFor(schemaName: string, tableName: string): MerchantEntity | undefined {
  return MERCHANT_ENTITIES[`${schemaName}.${tableName}`];
}

/** Tables a merchant may see, as `schema.table` — the query's allowlist. */
export function merchantVisibleTables(): { schemaName: string; tableName: string }[] {
  return Object.keys(MERCHANT_ENTITIES).map((key) => {
    const [schemaName, tableName] = key.split('.');
    return { schemaName, tableName };
  });
}

/** `compareAtPrice` → "Compare at price", for columns with no explicit label. */
export function prettifyField(field: string): string {
  const spaced = field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
